import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { GameLayout } from './GameLayout';
import { categoryLabel, difficultyLabel, useQuizzes, type Lang, type Quiz } from '../data/quizzes';
import { QUIZ_TEXT, useQuizLang } from '../lib/quizLang';

const ALL = '__all__';

export function Quizzes() {
  const [lang] = useQuizLang();
  const [filter, setFilter] = useState(ALL);
  const t = QUIZ_TEXT[lang];

  const { quizzes } = useQuizzes(lang);
  const categories = useMemo(() => [ALL, ...Array.from(new Set(quizzes.map((q) => q.category)))], [quizzes]);
  const visible = useMemo(
    () => (filter === ALL ? quizzes : quizzes.filter((q) => q.category === filter)),
    [quizzes, filter],
  );

  return (
    <GameLayout>
      <QuizzesStyles />
      <div className="qz-topline">
        <span className="label">{t.crumb(quizzes.length)}</span>
      </div>
      <h1 className="qz-title">
        {t.titleA} <span className="accent-word">{t.titleB}</span>
      </h1>
      <p className="qz-lede">{t.lede(quizzes.length)}</p>

      <Link to="/quiz-live" className="qz-battle">
        <span className="qz-battle-title">🎮 {t.battleCta}</span>
        <span className="qz-battle-sub">{t.battleSub}</span>
      </Link>

      <div className="qz-tabs">
        {categories.map((c) => (
          <button key={c} className={`qz-tab${c === filter ? ' on' : ''}`} onClick={() => setFilter(c)}>
            {c === ALL ? t.all : categoryLabel(c, lang)}
          </button>
        ))}
      </div>

      <div className="qz-list">
        <div className="qz-head">
          <span className="label">
            {t.found}: {visible.length}
          </span>
          <span className="mono qz-sort">{t.sort}</span>
        </div>
        {visible.map((quiz, i) => (
          <QuizRow key={quiz.id} quiz={quiz} lang={lang} n={String(i + 1).padStart(2, '0')} />
        ))}
      </div>
    </GameLayout>
  );
}

function QuizRow({ quiz, n, lang }: { quiz: Quiz; n: string; lang: Lang }) {
  const t = QUIZ_TEXT[lang];
  return (
    <Link to={`/quiz/${quiz.id}`} className="qz-row">
      <span className="qz-num">{n}</span>
      <span className="qz-emoji">{quiz.emoji}</span>
      <div className="qz-body">
        <h3>{quiz.title}</h3>
        <div className="qz-desc">{quiz.desc}</div>
      </div>
      <div className="qz-meta">
        <span className="qz-cat">{categoryLabel(quiz.category, lang)}</span>
        <span className="qz-count">{t.questions(quiz.questions.length)}</span>
        <span className={`qz-diff ${quiz.difficulty}`}>{difficultyLabel(quiz.difficulty, lang)}</span>
      </div>
      <span className="qz-go">{t.play}</span>
      <div className="qz-hoverline" />
    </Link>
  );
}

function QuizzesStyles() {
  return (
    <style>{`
      .qz-topline { display: flex; align-items: center; justify-content: space-between; gap: 16px; }

      .qz-title { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 88px; line-height: 1.02; letter-spacing: -.015em; margin: 12px 0 0; }
      .qz-lede { max-width: 560px; font-size: 17px; line-height: 1.55; color: var(--ink-dim); margin: 24px 0 24px; }

      .qz-battle { display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px 18px; text-decoration: none; color: inherit;
        background: var(--bg2); border: 1px solid var(--line); border-radius: var(--radius-card); padding: 16px 20px; margin-bottom: 40px;
        transition: border-color .2s; }
      .qz-battle:hover { border-color: var(--accent); }
      .qz-battle-title { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 22px; letter-spacing: -.005em; }
      .qz-battle-sub { font-size: 13px; color: var(--ink-dim); }

      .qz-tabs { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 28px; }
      .qz-tab { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: .12em; text-transform: uppercase;
        background: transparent; color: var(--ink-dim); border: 1px solid var(--line); border-radius: 3px; padding: 8px 14px; cursor: pointer; transition: all .2s; }
      .qz-tab:hover { border-color: var(--line-strong); color: var(--ink); }
      .qz-tab.on { background: var(--accent); color: var(--accent-ink); border-color: var(--accent); }

      .qz-list { display: flex; flex-direction: column; }
      .qz-head { display: flex; justify-content: space-between; align-items: baseline; padding-bottom: 14px; border-bottom: 1px solid var(--line-strong); }
      .qz-sort { font-size: 11px; color: var(--ink-faint); letter-spacing: .14em; text-transform: uppercase; }

      .qz-row { position: relative; display: grid; grid-template-columns: 44px 56px 1fr 250px 90px; align-items: center; gap: 18px;
        padding: 22px 0; border-bottom: 1px solid var(--line); cursor: pointer; text-decoration: none; color: inherit;
        transition: background .2s, padding .2s; }
      .qz-row:hover { background: var(--bg2); padding-left: 16px; padding-right: 16px; }
      .qz-num { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: var(--ink-faint); letter-spacing: .16em; }
      .qz-emoji { font-size: 30px; line-height: 1; }
      .qz-body { min-width: 0; }
      .qz-body h3 { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 30px; line-height: 1; letter-spacing: -.005em; margin: 0 0 8px; }
      .qz-desc { font-size: 14px; color: var(--ink-dim); line-height: 1.5; max-width: 520px; }
      .qz-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 7px; }
      .qz-cat { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--ink-dim); }
      .qz-count { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: .08em; color: var(--ink-faint); }
      .qz-diff { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: .12em; text-transform: uppercase; padding: 4px 8px; border-radius: 3px; border: 1px solid var(--line-strong); color: var(--ink-dim); }
      .qz-diff.easy { color: #7ec96b; border-color: rgba(126, 201, 107, .4); }
      .qz-diff.medium { color: #e0c36a; border-color: rgba(224, 195, 106, .4); }
      .qz-diff.hard { color: #e0936a; border-color: rgba(224, 147, 106, .4); }
      .qz-diff.hardcore { color: #e06a6a; border-color: rgba(224, 106, 106, .4); }
      .qz-go { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--ink); letter-spacing: .12em; white-space: nowrap; }
      .qz-hoverline { position: absolute; bottom: -1px; left: 0; height: 2px; width: 0; background: var(--accent); transition: width .35s cubic-bezier(.2,.8,.2,1); }
      .qz-row:hover .qz-hoverline { width: 100%; }

      @media (max-width: 900px) { .qz-title { font-size: 56px; } }
      @media (max-width: 860px) {
        .qz-row { grid-template-columns: 44px 1fr; row-gap: 8px; }
        .qz-emoji { display: none; }
        .qz-meta { grid-column: 2; flex-direction: row; align-items: center; gap: 14px; }
        .qz-go { display: none; }
        .qz-body h3 { font-size: 26px; }
      }
    `}</style>
  );
}
